import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// One query-string parameter kept as state in the URL, so a refresh keeps the
// place, the browser's back button steps through selections, and the address
// can be sent to someone. Reading returns `defaultValue` when the parameter
// is absent; setting the default (or an empty value) removes the parameter,
// so default URLs stay clean. `set(value, { replace: true })` rewrites the
// current entry instead of adding one (for values that change on every
// keystroke, such as a search).
const useQueryParam = (name, defaultValue = '') => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const value = params.has(name) ? params.get(name) : defaultValue;

  const set = useCallback((next, { replace = false } = {}) => {
    const current = new URLSearchParams(history.location.search);
    const empty = next === null || next === undefined || next === '' || String(next) === String(defaultValue);
    if (empty) current.delete(name); else current.set(name, String(next));
    const search = current.toString();
    const target = { pathname: history.location.pathname, search: search ? `?${search}` : '' };
    const unchanged = target.search === (history.location.search || '');
    if (unchanged) return;
    if (replace) history.replace(target); else history.push(target);
  }, [history, name, defaultValue]);

  return [value, set];
};

export default useQueryParam;
